import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, TextField, Typography, InputAdornment, CircularProgress, IconButton, Snackbar, Alert, useMediaQuery, useTheme } from '@mui/material';
import { Search, Close, Restaurant } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { loadMenu } from '../lib/menu';
import { DEMO_TENANT_ID } from '../supabase';
import { MenuItemCard } from '../components/MenuItemCard';
import { ItemDetailModal } from '../components/ItemDetailModal';
import { useCart } from '../store/cart';
import { useLang, pickLocale } from '../i18n/LanguageProvider';
import { palette } from '../theme';
import type { Category, MenuItem } from '../types';

const fmt = (n: number) => `${n.toFixed(2).replace('.', ',')} EUR`;

export const MenuPage: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialCat = params.get('category') || '';

  const [categories, setCategories] = useState<Category[]>([]);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [activeCat, setActiveCat] = useState<string>(initialCat);
  const [openItem, setOpenItem] = useState<MenuItem | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [orderType, setOrderType] = useState<'delivery' | 'pickup'>(() => {
    try { return (localStorage.getItem('saheeh_order_type') as 'delivery' | 'pickup') || 'delivery'; }
    catch { return 'delivery'; }
  });

  useEffect(() => {
    try { localStorage.setItem('saheeh_order_type', orderType); } catch { /* ignore */ }
  }, [orderType]);

  const { add, lines, count, subtotal, setQuantity, remove } = useCart();
  const { lang, setLang, t } = useLang();

  const sectionRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await loadMenu(DEMO_TENANT_ID);
        if (!alive) return;
        setCategories(data.categories);
        setItems(data.items);
        if (!initialCat && data.categories.length > 0) setActiveCat(data.categories[0].id);
      } catch (e: any) {
        if (alive) setError(e?.message || 'Failed to load menu');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Group items by category for sectioned rendering, filtered by search.
  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const words = q.split(/\s+/).filter(Boolean);
    const filt = (it: MenuItem) => {
      if (!q) return true;
      const hay = `${it.name || ''} ${it.name_local || ''} ${it.description || ''} ${it.description_local || ''}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    };
    const m = new Map<string, MenuItem[]>();
    categories.forEach((c) => m.set(c.id, []));
    const uncategorized: MenuItem[] = [];
    items.forEach((it) => {
      if (!filt(it)) return;
      if (it.category_id && m.has(it.category_id)) m.get(it.category_id)!.push(it);
      else uncategorized.push(it);
    });
    return { m, uncategorized };
  }, [items, categories, query]);

  // Scrollspy: update activeCat based on which section is in view.
  useEffect(() => {
    if (loading || query) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        const top = visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        const id = (top.target as HTMLElement).dataset.catId;
        if (id) setActiveCat(id);
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: 0 },
    );
    sectionRefs.current.forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [grouped, loading, query]);

  const scrollToCat = (id: string) => {
    setActiveCat(id);
    const el = sectionRefs.current.get(id);
    if (el) {
      // The main column scrolls itself on desktop, page scrolls on mobile.
      const isInScroller = !!el.closest('[data-main-scroll]');
      if (isInScroller) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        const y = el.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
    params.set('category', id);
    setParams(params, { replace: true });
  };

  const onAdd = (it: MenuItem, qty: number, notes: string) => {
    add(it, qty, notes);
    setToast(`${qty} × ${pickLocale(it, lang)} → ${t('item.added')}`);
  };

  const onQuickAdd = (it: MenuItem) => {
    add(it, 1);
    setToast(`${pickLocale(it, lang)} → ${t('item.added')}`);
  };

  // Pre-compute category counts for the sidebar
  const totalCount = useMemo(
    () => Array.from(grouped.m.values()).reduce((s, arr) => s + arr.length, 0) + grouped.uncategorized.length,
    [grouped],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: palette.primary }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 8, textAlign: 'center', px: 2 }}>
        <Typography sx={{ color: palette.textMuted }}>{error}</Typography>
      </Box>
    );
  }

  // ── Shared bits ──────────────────────────────────────────────
  const SearchInput = (
    <TextField
      fullWidth
      placeholder={t('menu.search')}
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      size="small"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search sx={{ color: palette.textMuted, fontSize: 18 }} />
          </InputAdornment>
        ),
        endAdornment: query ? (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => setQuery('')} aria-label="clear search">
              <Close sx={{ color: palette.textMuted, fontSize: 16 }} />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'rgba(255,255,255,0.05)',
          borderRadius: '10px',
          '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
          '&:hover fieldset': { borderColor: palette.borderHover },
          '&.Mui-focused fieldset': { borderColor: palette.primary },
        },
        '& .MuiInputBase-input': { color: '#fff', fontSize: '14px', py: 1.2 },
      }}
    />
  );

  // ── Sidebar (left) ───────────────────────────────────────────
  const Sidebar = (
    <Box
      component="aside"
      sx={{
        position: { md: 'sticky' },
        top: { md: 0 },
        alignSelf: 'start',
        height: { xs: 'auto', md: '100dvh' },
        padding: { xs: 2, md: '28px 20px' },
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        background: palette.bgElev,
        borderRight: { md: `1px solid rgba(255,255,255,0.04)` },
        overflowY: { md: 'auto' },
      }}
    >
      {/* Brand */}
      <Box
        onClick={() => navigate('/')}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.2,
          pb: 1.5, borderBottom: '1px solid rgba(255,255,255,0.04)',
          justifyContent: 'center', cursor: 'pointer',
        }}
      >
        <Box
          component="img"
          src="/saheeh_logo_w.svg"
          alt="Saheeh"
          sx={{ height: 44, width: 'auto', display: 'block' }}
        />
        <Box>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '1.25rem', color: '#fff', letterSpacing: '-0.3px', lineHeight: 1 }}>
            SAHEEH
          </Typography>
          <Typography sx={{ fontSize: '0.62rem', letterSpacing: 2, color: palette.textMuted, textTransform: 'uppercase', mt: 0.3 }}>
            · صحيح · Order
          </Typography>
        </Box>
      </Box>

      {/* Order type */}
      <Box>
        <Typography sx={{ fontSize: '0.66rem', letterSpacing: '1.6px', textTransform: 'uppercase', color: palette.textMuted, px: 0.5, mb: 0.8 }}>
          Order Type
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, background: 'rgba(255,255,255,0.03)', p: 0.5, borderRadius: '12px' }}>
          {(['delivery', 'pickup'] as const).map((m) => (
            <Box
              key={m}
              onClick={() => setOrderType(m)}
              sx={{
                flex: 1, py: 1.1, borderRadius: '8px', textAlign: 'center',
                cursor: 'pointer', fontWeight: 600, fontSize: '0.84rem',
                color: orderType === m ? palette.bg : palette.textMuted,
                background: orderType === m ? palette.primary : 'transparent',
                transition: 'all 0.15s',
              }}
            >
              {m === 'delivery' ? '🚴 Delivery' : '🏠 Pickup'}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Search */}
      <Box>
        <Typography sx={{ fontSize: '0.66rem', letterSpacing: '1.6px', textTransform: 'uppercase', color: palette.textMuted, px: 0.5, mb: 0.8 }}>
          Menu
        </Typography>
        {SearchInput}
      </Box>

      {/* Categories */}
      {!query && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, flex: 1, overflowY: { md: 'auto' } }}>
          <CatBtn label={t('menu.all') || 'All'} count={totalCount} active={!activeCat} onClick={() => { setActiveCat(''); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
          {categories.map((c) => {
            const cnt = grouped.m.get(c.id)?.length || 0;
            if (cnt === 0) return null;
            return (
              <CatBtn
                key={c.id}
                label={pickLocale(c, lang)}
                count={cnt}
                active={activeCat === c.id}
                onClick={() => scrollToCat(c.id)}
              />
            );
          })}
        </Box>
      )}

      {/* Bottom: back + lang */}
      <Box sx={{ display: 'flex', gap: 1, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.04)', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box
          onClick={() => navigate('/')}
          sx={{
            cursor: 'pointer', color: palette.primary, fontSize: '0.88rem', fontWeight: 600,
            px: 1.5, py: 0.8, borderRadius: '8px', '&:hover': { background: 'rgba(255,255,255,0.04)' },
          }}
        >
          ← {lang === 'ar' ? 'رجوع' : 'Back'}
        </Box>
        <Box sx={{ display: 'flex', gap: 0, border: `1px solid ${palette.border}`, borderRadius: '10px', overflow: 'hidden' }}>
          {(['en', 'ar'] as const).map((l) => (
            <Box
              key={l}
              onClick={() => setLang(l)}
              sx={{
                px: 1.3, py: 0.5, cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem',
                color: lang === l ? palette.bg : palette.textMuted,
                background: lang === l ? palette.primary : 'transparent',
                transition: 'all 0.15s',
              }}
            >
              {l.toUpperCase()}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  // ── Main (center) ────────────────────────────────────────────
  const renderSections = () => {
    if (query) {
      // Flat filtered grid
      const all: MenuItem[] = [];
      categories.forEach((c) => {
        const arr = grouped.m.get(c.id) || [];
        arr.forEach((it) => all.push(it));
      });
      grouped.uncategorized.forEach((it) => all.push(it));

      if (all.length === 0) {
        return (
          <Box sx={{ textAlign: 'center', py: 8, color: palette.textMuted }}>
            <Typography sx={{ fontWeight: 600 }}>{t('menu.empty_title')}</Typography>
            <Typography sx={{ fontSize: '13px' }}>{t('menu.empty_subtitle')}</Typography>
          </Box>
        );
      }

      // Build a name lookup so we can pass categoryName to the card
      const catNameById = new Map(categories.map((c) => [c.id, c.name]));
      return (
        <Box sx={{ mb: 6 }}>
          <CategoryTitle>{all.length} {lang === 'ar' ? 'نتائج' : 'results'}</CategoryTitle>
          <Grid>
            {all.map((it) => (
              <MenuItemCard
                key={it.id}
                item={it}
                categoryName={it.category_id ? catNameById.get(it.category_id) : undefined}
                onClick={setOpenItem}
                onAdd={onQuickAdd}
              />
            ))}
          </Grid>
        </Box>
      );
    }

    return (
      <>
        {categories.map((cat) => {
          const list = grouped.m.get(cat.id) ?? [];
          if (list.length === 0) return null;
          return (
            <Box
              key={cat.id}
              ref={(el: HTMLDivElement | null) => { sectionRefs.current.set(cat.id, el); }}
              data-cat-id={cat.id}
              sx={{ mb: 6, scrollMarginTop: { xs: 80, md: 24 } }}
            >
              <CategoryTitle>{pickLocale(cat, lang)}</CategoryTitle>
              <Grid>
                {list.map((it) => (
                  <MenuItemCard
                    key={it.id}
                    item={it}
                    categoryName={cat.name}
                    onClick={setOpenItem}
                    onAdd={onQuickAdd}
                  />
                ))}
              </Grid>
            </Box>
          );
        })}
        {grouped.uncategorized.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <CategoryTitle>{t('menu.all') || 'All'}</CategoryTitle>
            <Grid>
              {grouped.uncategorized.map((it) => (
                <MenuItemCard key={it.id} item={it} onClick={setOpenItem} onAdd={onQuickAdd} />
              ))}
            </Grid>
          </Box>
        )}
      </>
    );
  };

  const Main = (
    <Box
      data-main-scroll
      sx={{
        padding: { xs: 2, md: '28px 28px 48px' },
        overflowY: { md: 'auto' },
        height: { md: '100dvh' },
      }}
    >
      {renderSections()}
    </Box>
  );

  // ── Cart (right) ─────────────────────────────────────────────
  const CartPanel = (
    <Box
      component="aside"
      sx={{
        position: { md: 'sticky' },
        top: { md: 0 },
        alignSelf: 'start',
        height: { md: '100dvh' },
        display: 'flex',
        flexDirection: 'column',
        background: palette.bgElev,
        borderLeft: { md: '1px solid rgba(255,255,255,0.04)' },
      }}
    >
      <Box sx={{ p: '22px 24px 14px', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
          🛒 {lang === 'ar' ? 'السلة' : 'Cart'}
        </Typography>
        <Box sx={{ fontSize: '0.78rem', px: 1.2, py: 0.4, borderRadius: 999, background: 'rgba(255,255,255,0.06)', color: palette.textMuted, fontWeight: 600 }}>
          {count} {count === 1 ? 'item' : 'items'}
        </Box>
      </Box>

      {lines.length === 0 ? (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', p: '40px 24px', color: palette.textMuted }}>
          <Restaurant sx={{ fontSize: 48, opacity: 0.4, mb: 1.5 }} />
          <Typography sx={{ fontWeight: 600, color: '#fff', mb: 0.5 }}>
            Your cart is empty.
          </Typography>
          <Typography sx={{ fontSize: '0.82rem' }}>
            Pick something delicious from the menu.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflowY: 'auto', p: '16px 20px', display: 'flex', flexDirection: 'column', gap: 1.2 }}>
          {lines.map((l) => (
            <Box
              key={l.menu_item_id + (l.notes || '')}
              sx={{
                display: 'grid', gridTemplateColumns: '36px minmax(0,1fr) auto', alignItems: 'center',
                gap: 1.2, p: 1.2, background: 'rgba(255,255,255,0.03)', borderRadius: '12px', fontSize: '0.88rem',
              }}
            >
              <Box sx={{ background: palette.primary, color: palette.bg, fontWeight: 800, fontSize: '0.85rem', textAlign: 'center', borderRadius: '8px', py: 0.5 }}>
                {l.quantity}×
              </Box>
              <Box>
                <Typography sx={{ color: '#fff', fontSize: '0.88rem', lineHeight: 1.3 }}>
                  {l.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.6 }}>
                  <Box
                    onClick={() => l.quantity <= 1 ? remove(l.menu_item_id) : setQuantity(l.menu_item_id, l.quantity - 1)}
                    sx={{ cursor: 'pointer', px: 1, py: 0.2, borderRadius: '6px', background: 'rgba(255,255,255,0.06)', color: palette.textMuted, fontSize: '0.8rem', minWidth: 26, textAlign: 'center' }}
                  >{l.quantity <= 1 ? '🗑' : '−'}</Box>
                  <Box
                    onClick={() => setQuantity(l.menu_item_id, l.quantity + 1)}
                    sx={{ cursor: 'pointer', px: 1, py: 0.2, borderRadius: '6px', background: 'rgba(255,255,255,0.06)', color: palette.textMuted, fontSize: '0.8rem', minWidth: 26, textAlign: 'center' }}
                  >+</Box>
                </Box>
              </Box>
              <Typography sx={{ color: palette.primary, fontWeight: 800, fontSize: '0.9rem' }}>
                {fmt(l.unit_price * l.quantity)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ p: '18px 20px 22px', borderTop: '1px solid rgba(255,255,255,0.04)', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Typography sx={{ color: palette.textMuted, fontSize: '0.95rem' }}>{lang === 'ar' ? 'المجموع' : 'Total'}</Typography>
          <Typography sx={{ color: palette.primary, fontWeight: 800, fontSize: '1.5rem' }}>{fmt(subtotal)}</Typography>
        </Box>
        <Box
          onClick={() => count > 0 && navigate('/checkout')}
          sx={{
            py: 1.5, borderRadius: '12px', textAlign: 'center',
            background: count > 0 ? palette.primary : 'rgba(255,255,255,0.06)',
            color: count > 0 ? palette.bg : palette.textMuted,
            fontWeight: 800, fontSize: '0.98rem',
            cursor: count > 0 ? 'pointer' : 'not-allowed',
            transition: 'all 0.15s',
            '&:hover': count > 0 ? { background: palette.primaryDark } : {},
          }}
        >
          Checkout →
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: { xs: 'block', md: 'grid' },
        gridTemplateColumns: { md: '240px minmax(0, 1fr) 340px' },
        background: palette.bg,
        minHeight: '100dvh',
      }}
    >
      {Sidebar}
      {Main}
      {isDesktop && CartPanel}

      <ItemDetailModal
        open={!!openItem}
        item={openItem}
        categoryName={openItem?.category_id ? categories.find((c) => c.id === openItem.category_id)?.name : undefined}
        onClose={() => setOpenItem(null)}
        onAdd={onAdd}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={1800}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          icon={false}
          sx={{
            background: `linear-gradient(135deg, ${palette.primary} 0%, ${palette.primaryDark} 100%)`,
            color: palette.bg,
            fontWeight: 600,
            border: 'none',
            '& .MuiAlert-message': { color: palette.bg },
          }}
        >
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ── Helpers ──
const CategoryTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    dir="auto"
    sx={{
      fontWeight: 800,
      fontSize: { xs: '24px', sm: '28px' },
      letterSpacing: '-0.5px',
      color: '#fff',
      mb: 2.5,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      '&::before': {
        content: '""',
        display: 'inline-block',
        width: 28,
        height: 2,
        background: palette.primary,
        borderRadius: 2,
      },
    }}
  >
    {children}
  </Typography>
);

const Grid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: 'repeat(2, 1fr)',
        sm: 'repeat(auto-fill, minmax(180px, 1fr))',
        md: 'repeat(3, 1fr)',
        lg: 'repeat(auto-fill, minmax(200px, 1fr))',
      },
      gap: { xs: 1.5, sm: 2 },
    }}
  >
    {children}
  </Box>
);

const CatBtn: React.FC<{ label: string; count: number; active: boolean; onClick: () => void }> = ({ label, count, active, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1.2,
      px: 1.5,
      py: 1.1,
      borderRadius: '10px',
      cursor: 'pointer',
      fontSize: '0.9rem',
      fontWeight: active ? 700 : 500,
      color: active ? '#fff' : palette.textMuted,
      background: active ? 'rgba(255,255,255,0.06)' : 'transparent',
      transition: 'background 0.15s, color 0.15s',
      '&:hover': { background: 'rgba(255,255,255,0.04)', color: '#fff' },
    }}
  >
    <Box dir="auto" sx={{ unicodeBidi: 'isolate' }}>{label}</Box>
    <Box
      sx={{
        fontSize: '0.72rem',
        padding: '2px 8px',
        borderRadius: 999,
        background: active ? palette.primary : 'rgba(255,255,255,0.05)',
        color: active ? palette.bg : palette.textMuted,
        fontWeight: 700,
      }}
    >
      {count}
    </Box>
  </Box>
);
